import React, { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { api } from '../api/client';
import { COLORS, Panel, Button, Field, inputStyle, StatTile, Spinner, Banner, money, date } from '../components/ui';

/**
 * Review one PM programme: every site attached to it, the state of that site's
 * work order, and the detail behind it without leaving the page.
 *
 * Sites come from PMAsset rather than from work orders, so a programme that has
 * generated nothing shows its sites with an empty work-order column instead of
 * an empty page — that gap is the point.
 */

const STATUS_TONE = {
    CLOSED: { bg: '#e4f6e4', fg: '#046804' },
    COMPLETE: { bg: '#e4f6e4', fg: '#046804' },
    ISSUED: { bg: '#e8f1fc', fg: '#1c5cab' },
    REQUESTED: { bg: '#f2f1ee', fg: '#52514e' },
    ONHOLD: { bg: '#fff4d9', fg: '#8a6100' },
    CANCELED: { bg: '#f7f2f2', fg: '#898781' },
    DENIED: { bg: '#fbe9e9', fg: '#a12626' },
};

const AUTH_LABEL = {
    NOTREQUIRED: { text: 'not required', tone: { bg: '#f2f1ee', fg: '#898781' } },
    REQUIRED1: { text: 'awaiting approval', tone: { bg: '#fff4d9', fg: '#8a6100' } },
    APPROVED: { text: 'approved', tone: { bg: '#e4f6e4', fg: '#046804' } },
    REJECTED: { text: 'rejected', tone: { bg: '#fbe9e9', fg: '#a12626' } },
};

function Pill({ text, tone, title }) {
    return (
        <span title={title} style={{
            background: tone.bg, color: tone.fg, fontSize: 11, fontWeight: 600,
            padding: '2px 8px', borderRadius: 10, whiteSpace: 'nowrap',
        }}>{text}</span>
    );
}

/** Expanded work-order detail, pulled from the existing detail endpoint. */
function WoDetail({ wopk }) {
    const [d, setD] = useState(null);
    const [err, setErr] = useState(null);

    useEffect(() => {
        let live = true;
        api.getWorkOrder(wopk)
            .then(r => { if (live) setD(r); })
            .catch(e => { if (live) setErr(e.message); });
        return () => { live = false; };
    }, [wopk]);

    if (err) return <Banner tone="danger">{err}</Banner>;
    if (!d) return <Spinner label="Loading work order" />;

    const wo = d.workOrder;
    const section = (title, children) => (
        <div style={{ minWidth: 240, flex: '1 1 260px' }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: COLORS.muted, textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 6 }}>{title}</div>
            {children}
        </div>
    );
    const line = (k, v) => (
        <div style={{ fontSize: 12.5, marginBottom: 3 }}>
            <span style={{ color: COLORS.muted }}>{k}: </span><span style={{ color: COLORS.ink }}>{v || '—'}</span>
        </div>
    );

    return (
        <div style={{ background: COLORS.plane, padding: 14, borderRadius: 6 }}>
            <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
                {section('Work order', <>
                    {line('ID', <Link to={`/wo/${wo.pk}`} style={{ color: COLORS.link }}>{wo.id}</Link>)}
                    {line('Status', `${wo.statusDesc || wo.status}${wo.authStatusDesc ? ` · auth ${wo.authStatusDesc}` : ''}`)}
                    {line('Asset', `${wo.assetId || ''} ${wo.assetName || ''}`.trim())}
                    {line('Account', `${wo.accountId || ''} ${wo.accountName || ''}`.trim())}
                    {line('Supervisor', wo.supervisorName)}
                </>)}
                {section('Dates', <>
                    {line('Requested', date(wo.requestedDate))}
                    {line('Target', date(wo.targetDate))}
                    {line('Issued', date(wo.issuedDate))}
                    {line('Complete', date(wo.completeDate))}
                    {line('Closed', date(wo.closedDate))}
                </>)}
                {section('Cost', <>
                    {line('Labour actual', money(wo.costLaborActual))}
                    {line('Misc actual', money(wo.costMiscActual))}
                    {line('Total actual', money(wo.costTotalActual))}
                    {line('Estimated', money(wo.costTotalEstimated))}
                    {line('Hours', wo.actualHours || '—')}
                </>)}
                {section(`Attendance (${d.laborActuals.length})`, d.laborActuals.length
                    ? d.laborActuals.map(l => (
                        <div key={l.pk} style={{ fontSize: 12.5, marginBottom: 3 }}>
                            {l.laborName || l.laborId} — {l.totalHours || 0}h {date(l.workDate)}
                        </div>
                    ))
                    : <div style={{ fontSize: 12.5, color: COLORS.muted }}>No labour recorded</div>)}
            </div>

            {wo.reason && (
                <div style={{ marginTop: 12, fontSize: 12.5 }}>
                    <span style={{ color: COLORS.muted }}>Reason: </span>{wo.reason}
                </div>
            )}

            <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', marginTop: 14 }}>
                {section(`Attachments (${d.documents.length + d.images.length})`,
                    (d.documents.length + d.images.length) === 0
                        ? <div style={{ fontSize: 12.5, color: COLORS.muted }}>Nothing attached</div>
                        : <>
                            {d.documents.map(doc => (
                                <div key={`d${doc.pk}`} style={{ fontSize: 12.5, marginBottom: 3 }}>
                                    {doc.url
                                        ? <a href={doc.url} target="_blank" rel="noreferrer" style={{ color: COLORS.link }}>{doc.name || 'document'}</a>
                                        : (doc.name || 'document')}
                                    <span style={{ color: COLORS.muted }}> · {doc.type}</span>
                                </div>
                            ))}
                            {d.images.map(img => (
                                <div key={`i${img.pk}`} style={{ fontSize: 12.5, marginBottom: 3 }}>
                                    <a href={img.url} target="_blank" rel="noreferrer" style={{ color: COLORS.link }}>{img.photo}</a>
                                    <span style={{ color: COLORS.muted }}> · {date(img.date)}</span>
                                </div>
                            ))}
                        </>)}
                {section(`Articles (${d.tasks.length})`, d.tasks.length
                    ? d.tasks.slice(0, 12).map(t => (
                        <div key={t.pk} style={{ fontSize: 12.5, marginBottom: 3 }}>
                            <span style={{ color: t.complete ? '#046804' : COLORS.muted }}>{t.complete ? '✓' : '○'}</span>{' '}
                            {t.action}
                        </div>
                    ))
                    : <div style={{ fontSize: 12.5, color: COLORS.muted }}>No checklist on this work order</div>)}
            </div>
        </div>
    );
}

export default function PmReviewPage() {
    const [searchParams, setSearchParams] = useSearchParams();
    const today = new Date().toISOString().substring(0, 10);
    const yearAgo = new Date(Date.now() - 365 * 86400000).toISOString().substring(0, 10);

    const [lookups, setLookups] = useState(null);
    const [pmpk, setPmpk] = useState(searchParams.get('pmpk') || '');
    const [from, setFrom] = useState(searchParams.get('from') || yearAgo);

    // The end of the window is written into the URL, so a link opened the next day
    // carried yesterday's date and silently hid every work order raised today —
    // which is precisely when someone is looking. A saved window never shortens
    // the visible range past the present.
    const savedTo = searchParams.get('to');
    const [to, setTo] = useState(savedTo && savedTo > today ? savedTo : today);
    const [only, setOnly] = useState('');
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [openRow, setOpenRow] = useState(null);

    const [lookupState, setLookupState] = useState('loading');
    useEffect(() => {
        // Silently swallowing this left an empty dropdown that looked broken with
        // no explanation. On the API-backed path it is also slow enough to need
        // saying so out loud.
        api.pmProgrammeList()
            .then(l => { setLookups(l); setLookupState('ready'); })
            .catch(e => { setLookupState(`failed: ${e.message}`); });
    }, []);

    const [costsLoading, setCostsLoading] = useState(false);

    /**
     * Cost arrives after the table. On the API path it is two calls per work
     * order, so waiting for it would hold up a 72-site programme entirely — and a
     * row cap meant large programmes silently showed no cost at all.
     */
    const loadCosts = useCallback(async (rows) => {
        const missing = rows.filter(r => r.WOPK && r.CostTotalEstimated === null).map(r => r.WOPK);
        if (!missing.length) return;
        setCostsLoading(true);
        try {
            // 100 is the server's per-request cap; it batches internally, so a
            // 71-site programme is a single round trip.
            for (let i = 0; i < missing.length; i += 100) {
                const chunk = missing.slice(i, i + 100);
                const { costs } = await api.pmCosts(chunk);
                const byPk = new Map(costs.map(c => [c.wopk, c]));
                // Merge as each chunk lands so the numbers fill in progressively.
                setData(d => d && ({
                    ...d,
                    rows: d.rows.map(r => {
                        const c = byPk.get(r.WOPK);
                        return c ? {
                            ...r,
                            CostTotalEstimated: c.estimated ?? r.CostTotalEstimated,
                            CostTotalActual: c.actual ?? r.CostTotalActual,
                            LabourLines: c.labourLines ?? r.LabourLines,
                            LabourHours: c.labourHours ?? r.LabourHours,
                        } : r;
                    }),
                }));
            }
        } catch { /* the table stands without cost */ }
        finally { setCostsLoading(false); }
    }, []);

    const load = useCallback((pk) => {
        if (!pk) return;
        setLoading(true); setError(null); setOpenRow(null);
        api.pmSites(pk, { from, to })
            .then(d => { setData(d); loadCosts(d.rows); })
            .catch(e => { setError(e.message); setData(null); })
            .finally(() => setLoading(false));
        setSearchParams({ pmpk: pk, from, to }, { replace: true });
    }, [from, to, setSearchParams, loadCosts]);

    useEffect(() => { if (pmpk) load(pmpk); /* eslint-disable-next-line */ }, []);

    const s = data?.summary;
    const FILTERS = [
        { key: '', label: 'All sites', match: () => true },
        { key: 'overdue', label: 'Overdue', match: r => r.DaysOverdue },
        { key: 'nowo', label: 'No work order', match: r => !r.WOPK },
        { key: 'approval', label: 'Awaiting approval', match: r => r.AuthStatus === 'REQUIRED1' },
        { key: 'open', label: 'Not complete', match: r => r.WOPK && !r.Complete && !r.Closed },
    ];
    const matcher = (FILTERS.find(f => f.key === only) || FILTERS[0]).match;
    const rows = (data?.rows || []).filter(matcher);

    return (
        <div>
            <h1 style={{ fontSize: 22, margin: '0 0 4px', color: COLORS.ink }}>Programme review</h1>
            <p style={{ color: COLORS.ink2, fontSize: 13, marginTop: 0 }}>
                Pick a programme to see every site on it. Click a row for the work order, its attendance and its attachments.
            </p>

            <div style={{ display: 'flex', gap: 14, alignItems: 'flex-end', flexWrap: 'wrap', marginBottom: 16 }}>
                <Field label="PM programme"
                    hint={lookupState === 'loading'
                        ? 'loading the programme list from the MC API — first load takes a few seconds'
                        : lookupState !== 'ready'
                            ? lookupState
                            : `${(lookups?.pms || []).length} programmes${lookups?.source === 'mc-api' ? ' (from the MC API)' : ''}`}>
                    <select value={pmpk} onChange={e => { setPmpk(e.target.value); load(e.target.value); }}
                        disabled={lookupState === 'loading'}
                        style={{ ...inputStyle, minWidth: 460 }}>
                        <option value="">
                            {lookupState === 'loading' ? 'Loading programmes…' : 'Select a programme…'}
                        </option>
                        {(() => {
                            const pms = lookups?.pms || [];
                            // Newest first inside each group — a PM you just built is at
                            // the top of the first group rather than lost in 59 alphabetical rows.
                            const recent = (a, b) => new Date(b.LastChanged || 0) - new Date(a.LastChanged || 0);
                            // On the API path there is no PM record, so a programme
                            // cannot be classified as disabled or asset-less at all.
                            // One flat list beats three groups that would all be wrong.
                            const sampled = pms.some(p => p.sampled);
                            const groups = sampled
                                ? [{ label: 'Programmes with recent work orders', of: () => true }]
                                : [
                                    { label: 'Generating', of: p => !p.ScheduleDisabled && p.Assets > 0 },
                                    { label: 'Schedule disabled', of: p => p.ScheduleDisabled },
                                    { label: 'No assets attached — cannot generate', of: p => !p.ScheduleDisabled && !p.Assets },
                                ];
                            return groups.map(g => {
                                const list = pms.filter(g.of).sort(recent);
                                if (!list.length) return null;
                                return (
                                    <optgroup key={g.label} label={`${g.label} (${list.length})`}>
                                        {list.map(p => {
                                            // Only claim what the active source can actually know.
                                            const bits = p.sampled
                                                ? [p.LastActivity
                                                    ? `last raised ${new Date(p.LastActivity).toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: '2-digit' })}`
                                                    : null]
                                                : [
                                                    `${p.Assets} asset${p.Assets === 1 ? '' : 's'}`,
                                                    p.WOsEver ? `${p.WOsEver} WOs` : 'never raised a WO',
                                                    p.LastChanged
                                                        ? `changed ${new Date(p.LastChanged).toLocaleDateString('en-AU', { month: 'short', year: '2-digit' })}`
                                                        : null,
                                                ];
                                            const detail = bits.filter(Boolean).join(' · ');
                                            return (
                                                <option key={p.PMPK} value={p.PMPK}>
                                                    {p.PMID} — {p.PMName}{detail ? ` · ${detail}` : ''}
                                                </option>
                                            );
                                        })}
                                    </optgroup>
                                );
                            });
                        })()}
                    </select>
                </Field>
                <Field label="From"><input type="date" value={from} onChange={e => setFrom(e.target.value)} style={inputStyle} /></Field>
                <Field label="To"><input type="date" value={to} onChange={e => setTo(e.target.value)} style={inputStyle} /></Field>
                <Button onClick={() => load(pmpk)} disabled={!pmpk}>Apply</Button>
            </div>

            {error && <Banner tone="danger">{error}</Banner>}
            {loading && <Spinner label="Reading MC" />}
            {!pmpk && !loading && <Banner tone="info">Select a programme above.</Banner>}

            {data && !loading && data.window?.clampedTo && (
                <Banner tone="info">
                    The saved window ended {date(data.window.clampedTo)}, which would have hidden anything
                    raised since. Extended to {date(data.window.to)}.
                </Banner>
            )}

            {data && !loading && (data.limitations || []).length > 0 && (
                <Banner tone="warn">
                    Served from the MC API — the database is not reachable from this host.
                    <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>
                        {data.limitations.map((l, i) => <li key={i} style={{ marginBottom: 2 }}>{l}</li>)}
                    </ul>
                </Banner>
            )}

            {data && !loading && (
                <>
                    <Panel title={`${data.pm.PMID} — ${data.pm.PMName}`}
                        subtitle={`${data.pm.FrequencyDesc || data.pm.Frequency || ''} · window ${date(data.window.from)} – ${date(data.window.to)}${data.pm.ScheduleDisabled ? ' · SCHEDULE DISABLED' : ''}`}>
                        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                            <StatTile label="Sites on PM" value={s.sites} />
                            <StatTile label="No work order" value={s.noWorkOrder}
                                tone={s.noWorkOrder ? 'bad' : 'good'} sub="in this window" />
                            <StatTile label="Complete" value={s.complete}
                                sub={s.completionPct == null ? null : `${s.completionPct}% of ${s.wos}`} />
                            <StatTile label="On schedule" value={s.onTimePct == null ? '—' : `${s.onTimePct}%`}
                                sub={s.targetLeadDays != null
                                    ? `${s.targetLeadDays}d target vs ${s.meanDaysToComplete ?? '—'}d actual`
                                    : `${s.completedOnTime} on time · ${s.completedLate} late`}
                                tone={s.onTimePct != null && s.onTimePct < 80 ? 'warn' : undefined} />
                            <StatTile label="Overdue" value={s.overdue} tone={s.overdue ? 'bad' : 'good'}
                                sub={s.worstDaysOverdue ? `worst ${s.worstDaysOverdue}d` : null} />
                            <StatTile label="Awaiting approval" value={s.awaitingApproval}
                                tone={s.awaitingApproval ? 'warn' : undefined} />
                            <StatTile label="Not issued" value={s.notIssued} tone={s.notIssued ? 'warn' : undefined} />
                            {/* Summed from the rows rather than the server summary, so it
                                keeps up as costs stream in. Estimated leads: actual is zero
                                until jobs are invoiced, so an actual-first tile reads $0
                                for a live programme. */}
                            <StatTile label="Cost estimated"
                                value={costsLoading ? '…' : money(data.rows.reduce((a, r) => a + (r.CostTotalEstimated || 0), 0))}
                                sub={`actual ${money(data.rows.reduce((a, r) => a + (r.CostTotalActual || 0), 0))}`} />
                            <StatTile label="Labour hours" value={s.labourHours || '—'}
                                sub={s.timedVisits ? `${s.timedVisits} timed` : 'nominal, not measured'} />
                        </div>
                    </Panel>

                    <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 12, flexWrap: 'wrap' }}>
                        {FILTERS.map(f => {
                            const n = (data.rows || []).filter(f.match).length;
                            const on = only === f.key;
                            return (
                                <button key={f.key || 'all'} onClick={() => setOnly(f.key)} style={{
                                    background: on ? COLORS.link : '#fff', color: on ? '#fff' : COLORS.ink2,
                                    border: `1px solid ${on ? COLORS.link : COLORS.axis}`,
                                    padding: '5px 12px', borderRadius: 14, fontSize: 12.5,
                                    fontWeight: on ? 600 : 400, cursor: 'pointer',
                                }}>{f.label} <span style={{ opacity: 0.75 }}>{n}</span></button>
                            );
                        })}
                        <span style={{ fontSize: 12, color: COLORS.muted, marginLeft: 4 }}>
                            showing {rows.length} of {data.rows.length}
                        </span>
                    </div>

                    <Panel style={{ padding: 0 }}>
                        <div style={{ overflow: 'auto', maxHeight: 660, border: `1px solid ${COLORS.grid}`, borderRadius: 6 }}>
                            <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0, fontSize: 12.5 }}>
                                <thead>
                                    <tr>
                                        {['Site', 'Name', 'State', 'Work order', 'Status', 'Approval',
                                            'Estimated', 'Actual', 'Scheduled', 'Overdue', 'Attendance', 'Evidence', ''].map(h => (
                                            <th key={h} style={{
                                                position: 'sticky', top: 0, zIndex: 2, background: COLORS.plane,
                                                textAlign: 'left', padding: '8px 10px', fontWeight: 600,
                                                color: COLORS.ink2, whiteSpace: 'nowrap',
                                                boxShadow: `inset 0 -1px 0 ${COLORS.axis}`,
                                            }}>{h}</th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {rows.map((r, i) => {
                                        const isOpen = openRow === r.ShopID;
                                        const tone = STATUS_TONE[r.Status] || { bg: '#f2f1ee', fg: COLORS.ink2 };
                                        const auth = AUTH_LABEL[r.AuthStatus];
                                        // Estimated, not actual: actual is present on ~18% of PM work
                                        // orders and stays at zero until the job is invoiced, so a
                                        // column driven by it reads as "no cost" for live work.
                                        const value = r.CostTotalEstimated;
                                        return (
                                            <React.Fragment key={r.ShopID}>
                                                <tr onClick={() => setOpenRow(isOpen ? null : r.ShopID)}
                                                    style={{
                                                        cursor: r.WOPK ? 'pointer' : 'default',
                                                        background: isOpen ? '#eef4fc' : (i % 2 ? '#fcfcfb' : '#fff'),
                                                        borderBottom: `1px solid ${COLORS.grid}`,
                                                    }}>
                                                    <td style={{ padding: '6px 10px', fontWeight: 600 }}>{r.ShopID}</td>
                                                    <td style={{ padding: '6px 10px', color: COLORS.ink2 }}>{r.ShopName}</td>
                                                    <td style={{ padding: '6px 10px', color: COLORS.muted }}>{r.RepairCenterID}</td>
                                                    <td style={{ padding: '6px 10px' }}>
                                                        {r.WOID
                                                            ? <Link to={`/wo/${r.WOPK}`} onClick={e => e.stopPropagation()} style={{ color: COLORS.link }}>{r.WOID}</Link>
                                                            : <span style={{ color: COLORS.critical, fontWeight: 600 }}>none raised</span>}
                                                    </td>
                                                    <td style={{ padding: '6px 10px' }}>
                                                        {r.Status ? <Pill text={r.StatusDesc || r.Status} tone={tone} /> : ''}
                                                    </td>
                                                    <td style={{ padding: '6px 10px' }}>
                                                        {auth ? <Pill text={auth.text} tone={auth.tone} /> : ''}
                                                    </td>
                                                    <td style={{ padding: '6px 10px', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                                                        {value ? money(value) : <span style={{ color: COLORS.muted }}>—</span>}
                                                    </td>
                                                    <td style={{ padding: '6px 10px', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                                                        {/* Zero is the norm until a job is invoiced, so it is
                                                            muted rather than shouting a column of $0.00. */}
                                                        {r.CostTotalActual
                                                            ? money(r.CostTotalActual)
                                                            : <span style={{ color: COLORS.muted }}>{r.WOPK ? '$0' : '—'}</span>}
                                                    </td>
                                                    <td style={{ padding: '6px 10px', whiteSpace: 'nowrap' }}>
                                                        {date(r.TargetDate) || <span style={{ color: COLORS.muted }}>{date(r.NextScheduledDate) || '—'}</span>}
                                                    </td>
                                                    <td style={{
                                                        padding: '6px 10px', textAlign: 'right', fontVariantNumeric: 'tabular-nums',
                                                        color: r.DaysOverdue > 90 ? COLORS.critical : r.DaysOverdue > 0 ? '#9c4620' : COLORS.ink,
                                                        fontWeight: r.DaysOverdue ? 700 : 400,
                                                        background: r.DaysOverdue > 90 ? '#fbe0e0' : r.DaysOverdue > 0 ? '#fdefe8' : undefined,
                                                    }}>{r.DaysOverdue ? `${r.DaysOverdue}d` : ''}</td>
                                                    <td style={{ padding: '6px 10px', color: COLORS.ink2, whiteSpace: 'nowrap' }}>
                                                        {r.LabourLines
                                                            ? `${r.LabourLines} line${r.LabourLines > 1 ? 's' : ''}${r.LabourHours ? ` · ${r.LabourHours}h` : ''}`
                                                            : <span style={{ color: COLORS.muted }}>not visited</span>}
                                                    </td>
                                                    <td style={{ padding: '6px 10px', whiteSpace: 'nowrap' }}>
                                                        {(() => {
                                                            const ev = (r.Photos || 0) + (r.EvidenceDocs || 0);
                                                            if (!r.WOPK) return '';
                                                            return ev
                                                                ? <span style={{ color: '#046804' }}>
                                                                    {ev} file{ev > 1 ? 's' : ''}
                                                                    {r.RequirementDocs ? <span style={{ color: COLORS.muted }}> · {r.RequirementDocs} req</span> : null}
                                                                </span>
                                                                : <span style={{ color: COLORS.critical, fontWeight: 600 }}>
                                                                    none
                                                                    {r.RequirementDocs ? <span style={{ color: COLORS.muted, fontWeight: 400 }}> · {r.RequirementDocs} req</span> : null}
                                                                </span>;
                                                        })()}
                                                    </td>
                                                    <td style={{ padding: '6px 10px', color: COLORS.muted }}>
                                                        {r.WOPK ? (isOpen ? '▾' : '▸') : ''}
                                                    </td>
                                                </tr>
                                                {isOpen && r.WOPK && (
                                                    <tr>
                                                        <td colSpan={13} style={{ padding: 10, background: '#fff', borderBottom: `1px solid ${COLORS.grid}` }}>
                                                            <WoDetail wopk={r.WOPK} />
                                                        </td>
                                                    </tr>
                                                )}
                                            </React.Fragment>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </Panel>

                    <div style={{ fontSize: 11.5, color: COLORS.muted, marginTop: -8, marginBottom: 20, lineHeight: 1.6 }}>
                        <strong>Attendance</strong> is nominal: {data.dataNotes.timeOnSite}{' '}
                        <strong>Value</strong>: {data.dataNotes.value}{' '}
                        <strong>Needs revisit</strong> is not shown — {data.dataNotes.needsRevisit}
                    </div>
                </>
            )}
        </div>
    );
}
